import { useMemo, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

export interface LiquidRailItem {
  id: string;
  label: string;
  icon: LucideIcon;
  section?: "primary" | "actions" | "utility";
  disabled?: boolean;
}

export interface LiquidRailTick {
  id: string;
  label: string;
  color?: string;
}

interface LiquidGlassRailProps {
  items: LiquidRailItem[];
  activeItem: string;
  onActiveItemChange: (itemId: string) => void;
  label: string;
  testId: string;
  ticks?: LiquidRailTick[];
  activeTick?: string;
  onActiveTickChange?: (tickId: string) => void;
  showLabels?: boolean;
  compact?: boolean;
  className?: string;
}

const REGULAR_METRICS = {
  itemSize: 44,
  itemGap: 6,
  sectionGap: 14,
} as const;

const COMPACT_METRICS = {
  itemSize: 36,
  itemGap: 6,
  sectionGap: 12,
} as const;

function sectionOffset(
  items: LiquidRailItem[],
  index: number,
  itemGap: number,
  sectionGap: number,
) {
  let offset = 0;
  for (let i = 1; i <= index; i += 1) {
    const previous = items[i - 1];
    const current = items[i];
    offset += previous?.section === current?.section ? itemGap : sectionGap;
  }
  return offset;
}

function RailTicks({
  ticks,
  activeTick,
  onActiveTickChange,
  compact,
}: {
  ticks: LiquidRailTick[];
  activeTick?: string;
  onActiveTickChange?: (tickId: string) => void;
  compact?: boolean;
}) {
  if (ticks.length === 0) return null;

  return (
    <div
      className={cn(
        "mb-3 flex flex-col items-center gap-1.5 border-b border-glass-stroke-subtle pb-3",
        compact ? "w-9" : "w-11",
      )}
      data-testid="liquid-rail-ticks"
    >
      {ticks.map((tick) => {
        const active = tick.id === activeTick;

        return (
          <button
            key={tick.id}
            type="button"
            className="group relative flex h-4 w-full items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/55"
            onClick={() => onActiveTickChange?.(tick.id)}
            aria-label={tick.label}
            aria-pressed={active}
            data-testid={`liquid-rail-tick-${tick.id}`}
          >
            <span
              className={cn(
                "rounded-full transition-all duration-150",
                active ? "h-1.5 w-4" : "h-1.5 w-1.5 group-hover:w-3",
                tick.color ?? "bg-glass-stroke-light",
              )}
              aria-hidden="true"
            />
            <span className="pointer-events-none absolute left-full top-1/2 ml-3 -translate-y-1/2 whitespace-nowrap rounded-full border border-glass-stroke-light bg-glass-fill-dense px-2.5 py-1 text-[11px] font-medium text-primary opacity-0 shadow-glass-sm backdrop-blur-glass-sm transition-opacity group-hover:opacity-100">
              {tick.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function RailButton({
  item,
  active,
  previewed,
  showLabels,
  compact,
  onSelect,
}: {
  item: LiquidRailItem;
  active: boolean;
  previewed: boolean;
  showLabels?: boolean;
  compact?: boolean;
  onSelect: () => void;
}) {
  const Icon = item.icon;

  return (
    <button
      type="button"
      className={cn(
        "group relative z-10 flex items-center justify-center rounded-full transition-[color,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/55",
        compact ? "h-9 w-9" : "h-11 w-11",
        showLabels && "flex-col",
        item.disabled
          ? "cursor-not-allowed text-muted/[0.35]"
          : "text-muted hover:text-primary",
        active && "text-primary",
        previewed && !active && "text-primary",
      )}
      onClick={onSelect}
      disabled={item.disabled}
      aria-label={item.label}
      aria-pressed={active}
      data-testid={`liquid-rail-item-${item.id}`}
    >
      <span
        className={cn(
          "flex items-center justify-center rounded-full transition-colors",
          compact ? "h-6 w-6" : "h-7 w-7",
          active ? "bg-glass-stroke-subtle" : previewed && "bg-glass-fill-heavy",
        )}
        aria-hidden="true"
      >
        <Icon
          className={cn(compact ? "h-4 w-4" : "h-[18px] w-[18px]")}
          strokeWidth={2.15}
        />
      </span>
      {showLabels && (
        <span className="mt-0.5 text-[8px] font-semibold leading-none">
          {item.label}
        </span>
      )}
      <span
        className={cn(
          "absolute bottom-1 left-1/2 h-0.5 w-0.5 -translate-x-1/2 rounded-full bg-accent transition-opacity",
          active ? "opacity-100" : "opacity-0",
        )}
        aria-hidden="true"
      />
      <span className="pointer-events-none absolute left-full top-1/2 ml-3 -translate-y-1/2 whitespace-nowrap rounded-full border border-glass-stroke-light bg-glass-fill-dense px-2.5 py-1 text-[11px] font-medium text-primary opacity-0 shadow-glass-sm backdrop-blur-glass-sm transition-opacity group-hover:opacity-100">
        {item.label}
      </span>
    </button>
  );
}

export function LiquidGlassRail({
  items,
  activeItem,
  onActiveItemChange,
  label,
  testId,
  ticks = [],
  activeTick,
  onActiveTickChange,
  showLabels = false,
  compact = false,
  className,
}: LiquidGlassRailProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const metrics = compact ? COMPACT_METRICS : REGULAR_METRICS;
  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.id === activeItem),
  );
  const hoverIndex = hoveredItem
    ? items.findIndex((item) => item.id === hoveredItem)
    : -1;
  const previewIndex = hoverIndex >= 0 ? hoverIndex : activeIndex;
  const safePreviewIndex =
    items.length > 0
      ? Math.max(0, Math.min(previewIndex, items.length - 1))
      : 0;

  const itemOffsets = useMemo(
    () =>
      items.map((_, index) =>
        sectionOffset(items, index, metrics.itemGap, metrics.sectionGap),
      ),
    [items, metrics.itemGap, metrics.sectionGap],
  );
  const indicatorTop =
    safePreviewIndex * metrics.itemSize + (itemOffsets[safePreviewIndex] ?? 0);
  const railHeight =
    items.length * metrics.itemSize +
    (itemOffsets[itemOffsets.length - 1] ?? 0);

  return (
    <nav
      className={cn(
        "denker-glass-rail relative inline-flex flex-col items-center rounded-full border border-glass-stroke-light bg-glass-fill/70 shadow-glass backdrop-blur-glass",
        compact ? "p-1.5" : "p-2",
        className,
      )}
      aria-label={label}
      data-testid={testId}
      onMouseLeave={() => setHoveredItem(null)}
    >
      <RailTicks
        ticks={ticks}
        activeTick={activeTick}
        onActiveTickChange={onActiveTickChange}
        compact={compact}
      />
      <div className="relative flex flex-col items-center" style={{ height: railHeight }}>
        <span
          className={cn(
            "pointer-events-none absolute left-0 z-0 rounded-full border border-glass-stroke-light bg-glass-fill-heavy shadow-[inset_0_1px_0_rgba(255,255,255,0.20),0_5px_16px_rgba(0,0,0,0.18)] transition-transform duration-200 ease-out",
            compact ? "h-9 w-9" : "h-11 w-11",
          )}
          style={{ transform: `translateY(${indicatorTop}px)` }}
          aria-hidden="true"
          data-testid={`${testId}-indicator`}
        />
        {items.map((item, index) => {
          const currentOffset = itemOffsets[index] ?? 0;
          const previousOffset = itemOffsets[index - 1] ?? 0;
          const marginTop =
            index === 0 ? 0 : currentOffset - previousOffset;
          const previewed = index === safePreviewIndex;

          return (
            <div
              key={item.id}
              style={{ marginTop }}
              onMouseEnter={() => {
                if (!item.disabled) setHoveredItem(item.id);
              }}
              onFocus={() => {
                if (!item.disabled) setHoveredItem(item.id);
              }}
              onBlur={() => setHoveredItem(null)}
            >
              <RailButton
                item={item}
                active={item.id === activeItem}
                previewed={previewed}
                showLabels={showLabels}
                compact={compact}
                onSelect={() => onActiveItemChange(item.id)}
              />
            </div>
          );
        })}
      </div>
    </nav>
  );
}
